import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaTaskV1Service, serviceName } from '../service';
import { HasNewTasksIO, ServiceQueries } from '../types';
import { useTaskV1Queries } from './use-task-v1-queries';
import { UseTaskV1QueryEndpointArgs, useTaskV1Query } from './use-task-v1-query';

/**
 * A hook that returns a query for the `HasNewTasks` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */

export const queryKeyBase_HasNewTasks = getSchemaQueryKey<ServiceQueries, 'HasNewTasks'>({
  serviceName,
  endpointName: 'HasNewTasks',
  request: {},
});

export const useHasNewTasksQuery = <E = unknown, D = HasNewTasksIO['output']>(
  args: UseTaskV1QueryEndpointArgs<'HasNewTasks', E, D>
) =>
  useTaskV1Query({
    endpointName: 'HasNewTasks',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `HasNewTasks` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useHasNewTasksQueries = <E = unknown, D = HasNewTasksIO['output']>(
  argsArr: UseTaskV1QueryEndpointArgs<'HasNewTasks', E, D>[]
) =>
  useTaskV1Queries(
    argsArr.map((args) => ({
      endpointName: 'HasNewTasks',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `HasNewTasks` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchHasNewTasks = () => {
  const queryClient = useQueryClient();

  const fetchHasNewTasks = async <E = unknown, D = HasNewTasksIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseTaskV1QueryEndpointArgs<'HasNewTasks', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      HasNewTasksIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'HasNewTasks'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        HasNewTasksIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'HasNewTasks'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'HasNewTasks'>({
          serviceName,
          endpointName: 'HasNewTasks',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaTaskV1Service.HasNewTasks(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchHasNewTasks;
};
